import React, { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, CheckCircle, XCircle, Clock, Calendar, MapPin, Edit2, Save, X, PlusCircle, Image as ImageIcon, Eye, Users, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { generateExport } from "@/utils/exportUtils";
import { useEventsStore, PendingEvent } from "@/data/eventsStore";
import { getEventMetrics } from "@/data/eventMetricsStore";



interface AdminEventsPageProps {
  eventType?: "partis" | "citoyens";
}

const labels = {
  fr: {
    title: "Validation des événements",
    subtitle: "Validez ou rejetez les événements proposés avant publication.",
    back: "Retour à l'accueil",
    pending: "En attente",
    approved: "Approuvé",
    rejected: "Rejeté",
    approve: "Approuver",
    reject: "Rejeter",
    noEvents: "Aucun événement en attente de validation.",
    submittedBy: "Soumis par",
    on: "le",
    filterAll: "Tous",
    filterPending: "En attente",
    filterApproved: "Approuvés",
    filterRejected: "Rejetés",
    edit: "Modifier",
    save: "Enregistrer",
    cancel: "Annuler",
    rejectionReason: "Motif de rejet",
    rejectionPlaceholder: "Veuillez préciser le motif du rejet (optionnel)",
    submitRejection: "Confirmer le rejet",
    addEventBtn: "Ajouter un événement",
    addEventTitle: "Créer un événement",
    orgStatus: "Statut de l'organisateur",
    orgName: "Organisation/Parti",
    type: "Type d'événement",
    regType: "Accès",
    imageUrl: "URL de l'image (optionnel)",
  },
  ar: {
    title: "التحقق من الفعاليات",
    subtitle: "وافقوا أو ارفضوا الفعاليات المقترحة قبل النشر.",
    back: "العودة للرئيسية",
    pending: "قيد الانتظار",
    approved: "موافق عليه",
    rejected: "مرفوض",
    approve: "الموافقة",
    reject: "الرفض",
    noEvents: "لا توجد فعاليات قيد الانتظار.",
    submittedBy: "مقدم من",
    on: "بتاريخ",
    filterAll: "الكل",
    filterPending: "قيد الانتظار",
    filterApproved: "الموافق عليها",
    filterRejected: "المرفوضة",
    edit: "تعديل",
    save: "حفظ",
    cancel: "إلغاء",
    rejectionReason: "سبب الرفض",
    rejectionPlaceholder: "يرجى تحديد سبب الرفض (اختياري)",
    submitRejection: "تأكيد الرفض",
    addEventBtn: "إضافة فعالية",
    addEventTitle: "إنشاء فعالية",
    orgStatus: "صفة المنظم",
    orgName: "المنظمة/الحزب",
    type: "نوع الفعالية",
    regType: "الولوج",
    imageUrl: "رابط الصورة (اختياري)",
  },
  zgh: {
    title: "ⴰⵙⴼⵜⵉ ⵏ ⵉⵎⵓⵙⵙⵓⵜⵏ",
    subtitle: "ⵙⴼⵜⵉⵜ ⵏⵖⴷ ⴰⴳⵉⵜ ⵉⵎⵓⵙⵙⵓⵜⵏ ⵉⵜⵜⵓⵙⴽⴰⵏⵏ ⴷⴰⵜ ⵏ ⵓⵙⴼⵇⵇⵔ.",
    back: "ⵓⵖⴰⵍ ⵙ ⵓⵙⵏⵓⴱⴳ",
    pending: "ⴰⵔ ⵉⵜⵜⵔⴰⵊⵓ",
    approved: "ⵉⵜⵜⵓⵇⴱⵍ",
    rejected: "ⵉⵜⵜⵓⴳⵉ",
    approve: "ⵇⴱⵍ",
    reject: "ⴰⴳⵉ",
    noEvents: "ⵓⵔ ⵍⵍⵉⵏ ⵉⵎⵓⵙⵙⵓⵜⵏ ⴰⵔ ⵜⵜⵔⴰⵊⵓⵏ.",
    submittedBy: "ⵙⴳ",
    on: "ⴰⵙⵙ ⵏ",
    filterAll: "ⵎⴰⵕⵕⴰ",
    filterPending: "ⴰⵔ ⵜⵜⵔⴰⵊⵓⵏ",
    filterApproved: "ⵉⵜⵜⵓⵇⴱⵍⵏ",
    filterRejected: "ⵉⵜⵜⵓⴳⵉⵏ",
    edit: "ⵙⵏⴼⵍ",
    save: "ⵃⴹⵓ",
    cancel: "ⵙⵔⴼ",
    rejectionReason: "ⴰⵎⵏⵜⵉⵍ ⵏ ⵓⴳⴰⵢ",
    rejectionPlaceholder: "ⵎⵎⵍ ⴰⵎⵏⵜⵉⵍ ⵏ ⵓⴳⴰⵢ (ⵜⵉⵔⵉⵜ)",
    submitRejection: "ⵙⵙⵉⴷⴷ ⵓⴳⴰⵢ",
    addEventBtn: "ⵙⵉⴷⴼ ⴰⵎⵓⵙⵙⵓ",
    addEventTitle: "ⵙⵏⴼⵍ ⴰⵎⵓⵙⵙⵓ",
    orgStatus: "ⴰⴷⴷⴰⴷ ⵏ ⵓⵎⵙⵏⵎⴰⵍⴰ",
    orgName: "ⵜⴰⵎⵙⵎⵓⵏⵜ/ⴰⵎⵓⵔ",
    type: "ⴰⵏⴰⵡ",
    regType: "ⴰⴽⵛⵛⵓⵎ",
    imageUrl: "ⵜⴰⵡⵍⴰⴼⵜ",
  },
};

const AdminEventsPage: React.FC<AdminEventsPageProps> = ({ eventType = "partis" }) => {
  const { lang } = useI18n();
  const l = labels[lang];
  const { events, fetchEvents, saveEvent, updateEventStatus, deleteEvent, isLoading } = useEventsStore();
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [regTypeFilter, setRegTypeFilter] = useState<"all" | "public" | "mandatory">("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [partyFilter, setPartyFilter] = useState<string>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<PendingEvent>>({});
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [eventToReject, setEventToReject] = useState<string | null>(null);
  const [previewEvent, setPreviewEvent] = useState<PendingEvent | null>(null);
  const [viewRegistrationsEventId, setViewRegistrationsEventId] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);

  useEffect(() => {
    fetchEvents(false);
  }, [fetchEvents]);

  useEffect(() => {
    if (viewRegistrationsEventId) {
      const allRegs = JSON.parse(localStorage.getItem('eventRegistrations') || '[]');
      setRegistrations(allRegs.filter((r: any) => r.eventId === viewRegistrationsEventId));
    }
  }, [viewRegistrationsEventId]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [isImporting, setIsImporting] = useState(false);

  const handleMagicImport = async () => {
    if (!importUrl) return;
    setIsImporting(true);
    try {
      const { getApiUrl } = await import('@/utils/apiConfig');
      const res = await fetch(`${getApiUrl()}/magic_import.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: importUrl })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      
      setAddFormData(prev => ({
        ...prev,
        title: data.title || prev.title,
        description: data.description || prev.description,
        imageUrl: data.image || prev.imageUrl,
        date: new Date().toISOString().split('T')[0],
      }));
      setImportUrl("");
    } catch (err: any) {
      alert("Erreur d'importation : " + err.message);
    } finally {
      setIsImporting(false);
    }
  };
  const [addFormData, setAddFormData] = useState<Partial<PendingEvent>>({
    status: "approved",
    organizerStatus: "parti",
    partyAcronym: "",
    title: "",
    description: "",
    date: "",
    location: "",
    type: "meeting",
    registrationType: "public",
    imageUrl: "",
    contactName: "Administrateur",
    contactEmail: "admin@kaynin.ma",
  });

  const handleAddSubmit = async (e: React.FormEvent, status: "draft" | "published" = "published") => {
    e.preventDefault();
    if (!addFormData.title || !addFormData.description || !addFormData.date || !addFormData.location) {
      alert("Veuillez remplir les champs obligatoires.");
      return;
    }
    
    const result = await saveEvent({
       ...(addFormData as PendingEvent),
       status: status === "draft" ? "draft" : "approved",
    });
    
    if (!result) {
      alert("Erreur lors de la sauvegarde de l'événement. Veuillez réessayer.");
      return;
    }
    
    setAddModalOpen(false);
    setAddFormData({
      status: "approved",
      organizerStatus: "parti",
      partyAcronym: "",
      title: "",
      description: "",
      date: "",
      location: "",
      type: "meeting",
      registrationType: "public",
      registrationLink: undefined,
      externalLink: undefined,
      organizerLogo: undefined,
      imageUrl: "",
      contactName: "Administrateur",
      contactEmail: "admin@kaynin.ma",
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("L'image est trop grande (max 2Mo).");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAddFormData({ ...addFormData, imageUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const updateStatus = async (id: string, status: "approved" | "rejected", reason?: string) => {
    const success = await updateEventStatus(id, status, reason);
    if (!success) {
      alert("Erreur lors de la mise à jour du statut.");
    }
  };

  const handleEdit = (event: PendingEvent) => {
    setEditingId(event.id);
    setEditData(event);
  };

  const handleSave = async () => {
    if (editingId && editData.title) {
      const result = await saveEvent({ id: editingId, ...editData });
      if (result) {
        setEditingId(null);
      } else {
        alert("Erreur lors de la modification de l'événement.");
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleEditImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("L'image est trop grande (max 2Mo).");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditData({ ...editData, imageUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredByType = events.filter(e => {
    const orgStatus = e.organizerStatus || "parti";
    if (eventType === "partis") return orgStatus === "parti";
    return ["ong", "citoyen", "institution"].includes(orgStatus);
  });
  
  let filteredByStatus = filter === "all" ? filteredByType : filteredByType.filter(e => e.status === filter);
  
  // Apply month and party filters if in 'approved' view
  if (filter === "approved") {
    if (monthFilter !== "all") {
      filteredByStatus = filteredByStatus.filter(e => {
        const d = new Date(e.date);
        return d.getMonth().toString() === monthFilter;
      });
    }
    if (partyFilter !== "all") {
      filteredByStatus = filteredByStatus.filter(e => e.partyAcronym === partyFilter);
    }
  }

  const filtered = regTypeFilter === "all" ? filteredByStatus : filteredByStatus.filter(e => (e.registrationType || "public") === regTypeFilter);

  const statusConfig: Record<string, any> = {
    pending: { icon: Clock, color: "bg-accent/10 text-accent-foreground", label: l.pending },
    approved: { icon: CheckCircle, color: "bg-primary/10 text-primary", label: l.approved },
    rejected: { icon: XCircle, color: "bg-destructive/10 text-destructive", label: l.rejected },
    draft: { icon: Save, color: "bg-amber-100 text-amber-800", label: "Brouillon" },
  };

  const allMetrics = getEventMetrics();
  
  const orgStats = filtered.reduce((acc, ev) => {
    if (ev.status === "approved" || ev.status === "pending") {
      const likes = allMetrics[ev.id]?.like || 0;
      if (!acc[ev.partyAcronym]) {
         acc[ev.partyAcronym] = { name: ev.partyAcronym, likes: 0, events: 0 };
      }
      acc[ev.partyAcronym].events++;
      acc[ev.partyAcronym].likes += likes;
    }
    return acc;
  }, {} as Record<string, { name: string, likes: number, events: number }>);
  
  const orgStatsArray = Object.values(orgStats).sort((a,b) => b.likes - a.likes);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl mx-auto pb-10">
        <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
          <Link to="/"><ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />{l.back}</Link>
        </Button>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          <div className="text-center md:text-left flex-1">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
              <CheckCircle className="w-10 h-10 text-primary" />
              <h1 className="font-display font-extrabold text-3xl text-foreground">
                {l.title} {eventType === "partis" ? (lang === "ar" ? "للأحزاب" : lang === "zgh" ? "ⵏ ⵉⵎⵓⵔⴰⵏ" : "des partis") : (lang === "ar" ? "للمواطنين والمؤسسات" : lang === "zgh" ? "ⵏ ⵉⵏⴰⵎⵓⵔⵏ" : "citoyens et institutionnels")}
              </h1>
            </div>
            <p className="text-muted-foreground font-body">{l.subtitle}</p>
          </div>
          
          <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="font-body font-bold shadow-md shrink-0">
                <PlusCircle className="w-5 h-5 mr-2" />{l.addEventBtn}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{l.addEventTitle}</DialogTitle>
              </DialogHeader>
              <form className="space-y-4 py-4">
                <div className="bg-muted/30 p-3 rounded-lg mb-4 flex flex-col gap-2">
                  <Label className="font-body text-xs text-muted-foreground">Importer depuis un article (ex: Hespress, MAP...)</Label>
                  <div className="flex gap-2 items-center">
                    <Input placeholder="https://..." value={importUrl} onChange={(e) => setImportUrl(e.target.value)} className="font-body text-sm h-9" />
                    <Button type="button" size="sm" onClick={handleMagicImport} disabled={isImporting || !importUrl} className="shrink-0 h-9 font-body">
                      {isImporting ? "Importation..." : "✨ Import Magique"}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-body text-xs">{l.orgStatus} *</Label>
                    <Select required value={addFormData.organizerStatus} onValueChange={(v) => setAddFormData({...addFormData, organizerStatus: v})}>
                      <SelectTrigger className="font-body text-sm"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="parti">Parti politique</SelectItem>
                        <SelectItem value="ong">ONG</SelectItem>
                        <SelectItem value="institution">Institution</SelectItem>
                        <SelectItem value="citoyen">Citoyen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-xs">{l.orgName} *</Label>
                    {addFormData.organizerStatus === "parti" ? (
                      <Select required value={addFormData.partyAcronym} onValueChange={(v) => setAddFormData({...addFormData, partyAcronym: v})}>
                        <SelectTrigger className="font-body text-sm"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {allPartiesIncludingNonParliamentary.map(p => <SelectItem key={p.acronym} value={p.acronym}>{p.acronym}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input required value={addFormData.partyAcronym} onChange={(e) => setAddFormData({...addFormData, partyAcronym: e.target.value})} className="font-body text-sm"/>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-body text-xs">Titre *</Label>
                  <Input required value={addFormData.title} onChange={(e) => setAddFormData({...addFormData, title: e.target.value})} className="font-body text-sm"/>
                </div>

                <div className="space-y-2">
                  <Label className="font-body text-xs">Description *</Label>
                  <Textarea required value={addFormData.description} onChange={(e) => setAddFormData({...addFormData, description: e.target.value})} rows={3} className="font-body text-sm"/>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-body text-xs">Date *</Label>
                    <Input type="date" required value={addFormData.date} onChange={(e) => setAddFormData({...addFormData, date: e.target.value})} className="font-body text-sm"/>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-xs">Lieu *</Label>
                    <Input required value={addFormData.location} onChange={(e) => setAddFormData({...addFormData, location: e.target.value})} className="font-body text-sm"/>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-body text-xs">{l.type} *</Label>
                    <Select required value={addFormData.type} onValueChange={(v) => setAddFormData({...addFormData, type: v})}>
                      <SelectTrigger className="font-body text-sm"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conference">Conférence</SelectItem>
                        <SelectItem value="meeting">Réunion</SelectItem>
                        <SelectItem value="formation">Formation</SelectItem>
                        <SelectItem value="campagne">Campagne</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-xs">{l.regType} *</Label>
                    <Select required value={addFormData.registrationType} onValueChange={(v) => setAddFormData({...addFormData, registrationType: v as any})}>
                      <SelectTrigger className="font-body text-sm"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="mandatory">Sur inscription</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {addFormData.registrationType === 'mandatory' && (
                  <div className="space-y-2 bg-background p-3 rounded-lg border border-border">
                    <Label className="font-body text-xs">Lien ou Email d'inscription (Optionnel)</Label>
                    <Input placeholder="https://... ou contact@email.com" value={addFormData.registrationLink || ''} onChange={(e) => setAddFormData({...addFormData, registrationLink: e.target.value})} className="font-body text-sm" />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-body text-xs">Lien pour plus d'informations (Facultatif)</Label>
                  <Input placeholder="https://..." value={addFormData.externalLink || ''} onChange={(e) => setAddFormData({...addFormData, externalLink: e.target.value})} className="font-body text-sm" />
                </div>

                {addFormData.organizerStatus !== 'parti' && addFormData.organizerStatus !== 'media' && (
                  <div className="space-y-2">
                    <Label className="font-body text-xs">Logo de l'organisateur (Optionnel)</Label>
                    {!addFormData.organizerLogo ? (
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border bg-background">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                            <ImageIcon className="w-6 h-6 mb-2 opacity-50" />
                            <p className="text-xs font-body">Ajouter un logo</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => setAddFormData({ ...addFormData, organizerLogo: reader.result as string });
                              reader.readAsDataURL(file);
                            }
                          }} />
                        </label>
                      </div>
                    ) : (
                      <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-border group bg-background p-1">
                        <img src={addFormData.organizerLogo} alt="Logo" className="w-full h-full object-contain rounded" />
                        <Button type="button" variant="destructive" size="icon" className="absolute top-1 right-1 w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setAddFormData({ ...addFormData, organizerLogo: "" })}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-body text-xs">{l.imageUrl}</Label>
                  {!addFormData.imageUrl ? (
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                          <ImageIcon className="w-8 h-8 mb-3 opacity-50" />
                          <p className="mb-2 text-sm font-body"><span className="font-semibold">Cliquez</span> pour importer une image</p>
                          <p className="text-xs">SVG, PNG, JPG (MAX. 2Mo)</p>
                        </div>
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                  ) : (
                    <div className="relative w-full rounded-lg overflow-hidden border border-border group">
                      <img src={addFormData.imageUrl} alt="Prévisualisation" className="w-full h-auto object-contain bg-white max-h-64" />
                      <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 w-8 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setAddFormData({ ...addFormData, imageUrl: "" })}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                <DialogFooter className="pt-4 flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => setAddModalOpen(false)}>{l.cancel}</Button>
                  <Button type="button" variant="outline" className="border-orange-200 text-orange-600 bg-orange-50 hover:bg-orange-100" onClick={(e) => handleAddSubmit(e, "draft")}>Enregistrer Brouillon</Button>
                  <Button type="button" onClick={(e) => handleAddSubmit(e, "published")}>{l.save}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {(["all", "pending", "approved", "rejected", "draft"] as const).map(f => (
              <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)} className="font-body text-xs">
                {f === "all" ? l.filterAll : f === "pending" ? l.filterPending : f === "approved" ? l.filterApproved : f === "rejected" ? l.filterRejected : "Brouillons"}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {(["all", "public", "mandatory"] as const).map(rt => (
              <Button
                key={rt}
                variant={regTypeFilter === rt ? "secondary" : "outline"}
                size="sm"
                onClick={() => setRegTypeFilter(rt)}
                className={`font-body text-xs ${
                  rt === "public" ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" :
                  rt === "mandatory" ? "border-orange-300 text-orange-700 hover:bg-orange-50" : ""
                } ${regTypeFilter === rt && rt === "public" ? "bg-emerald-100" : regTypeFilter === rt && rt === "mandatory" ? "bg-orange-100" : ""}`}
              >
                {rt === "all" ? "📋 Tous accès" : rt === "public" ? "🟢 Public" : "🟠 Inscription requise"}
              </Button>
            ))}
          </div>
        </div>

        {filter === "approved" && (
          <div className="flex flex-wrap justify-center gap-2 mb-6 bg-muted/30 p-3 rounded-lg">
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs font-body"><SelectValue placeholder="Mois" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mois</SelectItem>
                {[...Array(12)].map((_, i) => (
                  <SelectItem key={i} value={i.toString()}>{new Date(0, i).toLocaleString('fr-FR', { month: 'long' })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={partyFilter} onValueChange={setPartyFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs font-body"><SelectValue placeholder={eventType === "partis" ? "Parti" : "Organisateur"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{eventType === "partis" ? "Tous les partis" : "Tous les organisateurs"}</SelectItem>
                {Array.from(new Set(events.filter(e => e.status === "approved").map(e => e.partyAcronym))).map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {orgStatsArray.length > 0 && filter === "all" && (
          <div className="mb-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
             {orgStatsArray.map(org => (
                <div key={org.name} className="bg-card border border-border p-3 rounded-xl flex flex-col justify-center items-center text-center shadow-sm relative overflow-hidden">
                   <div className="text-xs font-bold truncate w-full" title={org.name}>{org.name}</div>
                   <div className="text-[10px] text-muted-foreground mt-1">{org.events} {org.events > 1 ? "événements" : "événement"}</div>
                   <div className="mt-2 text-pink-600 font-bold flex items-center gap-1 text-sm bg-pink-50 px-2 py-0.5 rounded-md border border-pink-100">
                     ❤️ {org.likes}
                   </div>
                </div>
             ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-muted-foreground font-body text-center py-12">{l.noEvents}</p>
        ) : (
          <div className="space-y-4">
            {filtered.map(event => {
              const sc = statusConfig[event.status] || { icon: Clock, color: "bg-accent/10", label: event.status };
              const StatusIcon = sc.icon;
              return (
                <Card key={event.id} className="shadow-card border-border/50">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold text-primary-foreground bg-primary">
                          {event.partyAcronym.substring(0, 3)}
                        </span>
                        <span className="text-xs text-muted-foreground font-body">{event.partyAcronym}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {(event as any).isModified && (
                          <Badge className="bg-orange-500 hover:bg-orange-600 text-white text-[10px] font-bold border-none animate-pulse">
                            ÉVÉNEMENT MODIFIÉ
                          </Badge>
                        )}
                        {event.status === "draft" && (
                          <Badge className="bg-orange-100 text-orange-800 text-[10px] font-bold border-none">BROUILLON</Badge>
                        )}
                        {event.user_id === "system_scraper" && (
                          <Badge variant="outline" className="border-indigo-200 text-indigo-700 bg-indigo-50 shrink-0 font-body text-[10px]">
                            🤖 Trouvé sur le web
                          </Badge>
                        )}
                        <Badge className={`text-xs font-body ${sc.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {sc.label}
                        </Badge>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          (event.registrationType || "public") === "public"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-orange-100 text-orange-700"
                        }`}>
                          {(event.registrationType || "public") === "public" ? "🟢 Public" : "🟠 Inscription requise"}
                        </span>
                        {allMetrics[event.id] && allMetrics[event.id].like > 0 && (
                          <Badge className="bg-pink-100 text-pink-700 font-bold border-none ml-2 text-xs">
                            ❤️ {allMetrics[event.id].like}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {event.imageUrl && editingId !== event.id && (
                      <div className="mb-4 w-full h-48 sm:h-64 overflow-hidden rounded-md border border-border bg-white">
                        <img src={event.imageUrl} alt="Prévisualisation" className="w-full h-full object-contain" />
                      </div>
                    )}

                    {editingId === event.id ? (
                      <div className="space-y-4 mt-4 mb-3 bg-muted/20 p-4 rounded-lg border border-border">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">{l.orgStatus}</Label>
                            <Select value={editData.organizerStatus || 'parti'} onValueChange={(v) => setEditData({...editData, organizerStatus: v})}>
                              <SelectTrigger className="font-body text-xs h-8"><SelectValue/></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="parti">Parti politique</SelectItem>
                                <SelectItem value="ong">ONG</SelectItem>
                                <SelectItem value="institution">Institution</SelectItem>
                                <SelectItem value="citoyen">Citoyen</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">{l.orgName}</Label>
                            {(editData.organizerStatus || 'parti') === "parti" ? (
                              <Select value={editData.partyAcronym || ''} onValueChange={(v) => setEditData({...editData, partyAcronym: v})}>
                                <SelectTrigger className="font-body text-xs h-8"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                  {allPartiesIncludingNonParliamentary.map(p => <SelectItem key={p.acronym} value={p.acronym}>{p.acronym}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input value={editData.partyAcronym || ''} onChange={(e) => setEditData({...editData, partyAcronym: e.target.value})} className="font-body text-xs h-8"/>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="font-body text-[10px] uppercase text-muted-foreground">Titre</Label>
                          <Input value={editData.title || ''} onChange={(e) => setEditData({...editData, title: e.target.value})} className="font-display font-bold text-sm h-8" />
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="font-body text-[10px] uppercase text-muted-foreground">Description</Label>
                          <Textarea value={editData.description || ''} onChange={(e) => setEditData({...editData, description: e.target.value})} className="text-xs font-body" rows={3} />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">Date</Label>
                            <Input type="date" value={editData.date || ''} onChange={(e) => setEditData({...editData, date: e.target.value})} className="text-xs h-8" />
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">Lieu</Label>
                            <Input value={editData.location || ''} onChange={(e) => setEditData({...editData, location: e.target.value})} className="text-xs h-8" />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">{l.type}</Label>
                            <Select value={editData.type || 'meeting'} onValueChange={(v) => setEditData({...editData, type: v})}>
                              <SelectTrigger className="font-body text-xs h-8"><SelectValue/></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="conference">Conférence</SelectItem>
                                <SelectItem value="meeting">Réunion</SelectItem>
                                <SelectItem value="formation">Formation</SelectItem>
                                <SelectItem value="campagne">Campagne</SelectItem>
                                <SelectItem value="autre">Autre</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">{l.regType}</Label>
                            <Select value={editData.registrationType || 'public'} onValueChange={(v) => setEditData({...editData, registrationType: v as any})}>
                              <SelectTrigger className="font-body text-xs h-8"><SelectValue/></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="public">Public</SelectItem>
                                <SelectItem value="mandatory">Sur inscription</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {editData.registrationType === 'mandatory' && (
                          <div className="space-y-1 bg-background p-2 rounded border border-border">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">Lien ou Email d'inscription (Optionnel)</Label>
                            <Input placeholder="https://... ou contact@email.com" value={editData.registrationLink || ''} onChange={(e) => setEditData({...editData, registrationLink: e.target.value})} className="text-xs h-8" />
                          </div>
                        )}

                        <div className="space-y-1">
                          <Label className="font-body text-[10px] uppercase text-muted-foreground">Lien pour plus d'informations (Facultatif)</Label>
                          <Input placeholder="https://..." value={editData.externalLink || ''} onChange={(e) => setEditData({...editData, externalLink: e.target.value})} className="text-xs h-8" />
                        </div>

                        {editData.organizerStatus !== 'parti' && editData.organizerStatus !== 'media' && (
                          <div className="space-y-1">
                            <Label className="font-body text-[10px] uppercase text-muted-foreground">Logo de l'organisateur (Optionnel)</Label>
                            {!editData.organizerLogo ? (
                              <div className="flex items-center justify-center w-full">
                                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border bg-background">
                                  <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                                    <ImageIcon className="w-6 h-6 mb-2 opacity-50" />
                                    <p className="text-xs font-body">Ajouter un logo</p>
                                  </div>
                                  <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => setEditData({ ...editData, organizerLogo: reader.result as string });
                                      reader.readAsDataURL(file);
                                    }
                                  }} />
                                </label>
                              </div>
                            ) : (
                              <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-border group bg-background p-1">
                                <img src={editData.organizerLogo} alt="Logo" className="w-full h-full object-contain rounded" />
                                <Button type="button" variant="destructive" size="icon" className="absolute top-1 right-1 w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setEditData({ ...editData, organizerLogo: "" })}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="space-y-1">
                          <Label className="font-body text-[10px] uppercase text-muted-foreground">Photo de l'événement</Label>
                          {!editData.imageUrl ? (
                            <div className="flex items-center justify-center w-full">
                              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border bg-background">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                                  <ImageIcon className="w-6 h-6 mb-2 opacity-50" />
                                  <p className="text-xs font-body"><span className="font-semibold">Cliquez</span> pour ajouter une image</p>
                                </div>
                                <input type="file" className="hidden" accept="image/*" onChange={handleEditImageUpload} />
                              </label>
                            </div>
                          ) : (
                            <div className="relative w-full rounded-lg overflow-hidden border border-border group bg-background p-1">
                              <img src={editData.imageUrl} alt="Prévisualisation" className="w-full h-auto object-contain max-h-40 rounded" />
                              <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setEditData({ ...editData, imageUrl: "" })}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 justify-end pt-3 border-t border-border mt-4">
                          <Button size="sm" variant="outline" className="font-body text-xs" onClick={handleCancel}>
                            <X className="w-3.5 h-3.5 mr-1" />{l.cancel}
                          </Button>
                          <Button size="sm" className="font-body text-xs" onClick={handleSave}>
                            <Save className="w-3.5 h-3.5 mr-1" />{l.save}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="font-display font-bold text-foreground text-sm mb-2">{event.title}</h3>
                        <p className="text-muted-foreground text-xs font-body mb-3">{event.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground font-body mb-3">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{event.date}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.location}</span>
                        </div>
                      </>
                    )}

                    <div className="text-xs text-muted-foreground font-body mb-4 border-t border-border pt-3">
                      {l.submittedBy}: <span className="font-semibold text-foreground">{event.contactName}</span> ({event.contactEmail}) — {l.on} {new Date(event.submittedAt || event.createdAt || Date.now()).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>

                    {event.status === "rejected" && event.rejectionReason && (
                      <div className="mt-3 p-3 bg-destructive/5 rounded-md border border-destructive/20 text-xs font-body text-destructive">
                        <strong>{l.rejectionReason} :</strong> {event.rejectionReason}
                      </div>
                    )}

                    {event.status === "rejected" && (
                      <div className="flex flex-wrap gap-2 mt-4 border-t border-border pt-3">
                        <Button size="sm" variant="destructive" className="font-body text-xs" onClick={() => {
                          if (confirm("Supprimer définitivement cet événement ?")) deleteEvent(event.id);
                        }}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer définitivement
                        </Button>
                      </div>
                    )}

                    {editingId !== event.id && (event.status === "pending" || event.status === "draft" || event.status === "approved" || event.registrationType === "mandatory") && (
                      <div className="flex flex-wrap gap-2 mt-4 border-t border-border pt-3">
                        {(event.status === "pending" || event.status === "draft") && (
                          <>
                            <Button size="sm" variant="outline" className="font-body text-xs" onClick={() => handleEdit(event)}>
                              <Edit2 className="w-3.5 h-3.5 mr-1" />{l.edit}
                            </Button>
                            <Button size="sm" className="font-body text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateStatus(event.id, "approved")}>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />{l.approve}
                            </Button>
                            <Button size="sm" variant="destructive" className="font-body text-xs" onClick={() => {
                              if (event.status === "draft") {
                                if (confirm("Supprimer ce brouillon ?")) deleteEvent(event.id);
                              } else {
                                setEventToReject(event.id);
                                setRejectionReason("");
                                setRejectionModalOpen(true);
                              }
                            }}>
                              <XCircle className="w-3.5 h-3.5 mr-1" />{event.status === "draft" ? "Supprimer" : l.reject}
                            </Button>
                          </>
                        )}
                        {event.status === "approved" && event.publishStatus !== "draft" && (
                          <>
                            <Button size="sm" variant="outline" className="font-body text-xs" onClick={() => handleEdit(event)}>
                              <Edit2 className="w-3.5 h-3.5 mr-1" />Modifier
                            </Button>
                            <Button size="sm" variant="destructive" className="font-body text-xs" onClick={() => {
                              if (confirm("Êtes-vous sûr de vouloir supprimer cet événement validé ?")) {
                                deleteEvent(event.id);
                              }
                            }}>
                              <XCircle className="w-3.5 h-3.5 mr-1" />Supprimer
                            </Button>
                          </>
                        )}
                        {event.status === "draft" && (
                          <>
                            <Button size="sm" variant="outline" className="font-body text-xs border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => setPreviewEvent(event)}>
                              <Eye className="w-3.5 h-3.5 mr-1" />Prévisualiser
                            </Button>
                            <Button size="sm" variant="outline" className="font-body text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => saveEvent({ id: event.id, status: "approved" })}>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />Publier l'événement
                            </Button>
                          </>
                        )}
                        {event.registrationType === "mandatory" && (
                          <Button size="sm" variant="secondary" className="font-body text-xs ml-auto" onClick={() => setViewRegistrationsEventId(event.id)}>
                            <Users className="w-3.5 h-3.5 mr-1" />Liste des inscrits
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      {/* Modal Rejet */}
      <AlertDialog open={rejectionModalOpen} onOpenChange={setRejectionModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{l.reject}</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Précisez le motif du rejet de cet événement pour notifier le parti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Textarea
              placeholder={l.rejectionPlaceholder}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="font-body min-h-[100px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">{l.cancel}</AlertDialogCancel>
            <AlertDialogAction 
              className="font-body bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (eventToReject) {
                  updateStatus(eventToReject, "rejected", rejectionReason);
                }
                setRejectionModalOpen(false);
              }}
            >
              {l.submitRejection}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Prévisualisation Publique */}
      <Dialog open={!!previewEvent} onOpenChange={(open) => !open && setPreviewEvent(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none" aria-describedby="dialog-description">
          <DialogTitle className="sr-only">Prévisualisation de l'événement</DialogTitle>
          <div id="dialog-description" className="sr-only">Aperçu public de l'événement en cours de rédaction.</div>
          {previewEvent && (
            <div className="bg-background rounded-2xl overflow-hidden relative shadow-2xl">
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 px-4 py-1.5 rounded-full font-bold shadow-lg text-xs tracking-widest flex items-center gap-2 z-[101]">
                MODE PRÉVISUALISATION PUBLIQUE
              </div>
              <Button size="icon" variant="ghost" className="absolute top-4 right-4 z-[101] bg-black/50 hover:bg-black/70 text-white rounded-full" onClick={() => setPreviewEvent(null)}>
                 <X className="w-5 h-5" />
              </Button>

              {previewEvent.imageUrl ? (
                <div className="w-full h-64 md:h-80 relative bg-white">
                  <img src={previewEvent.imageUrl} alt={previewEvent.title} className="w-full h-full object-contain" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-8 pointer-events-none">
                     <div className="text-white pointer-events-auto">
                        <Badge className="bg-primary hover:bg-primary mb-3 text-white border-transparent">{previewEvent.type}</Badge>
                        <h2 className="text-3xl font-display font-extrabold">{previewEvent.title}</h2>
                     </div>
                  </div>
                </div>
              ) : (
                <div className="w-full pt-20 pb-10 px-8 bg-muted border-b border-border">
                  <Badge className="bg-primary hover:bg-primary mb-4 text-white border-transparent">{previewEvent.type}</Badge>
                  <h2 className="text-3xl font-display font-extrabold text-foreground">{previewEvent.title}</h2>
                </div>
              )}

              <div className="p-8 grid md:grid-cols-3 gap-8">
                 <div className="md:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-lg font-bold font-display mb-3">Description de l'événement</h3>
                      <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed font-body">{previewEvent.description}</p>
                    </div>
                 </div>
                 
                 <div className="space-y-6">
                    <Card className="shadow-sm border-border/50 bg-muted/30">
                      <CardContent className="p-5 space-y-4">
                        <div className="flex items-center gap-3">
                           <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                             <Calendar className="w-5 h-5" />
                           </div>
                           <div>
                             <p className="text-xs text-muted-foreground font-body">Date</p>
                             <p className="font-bold text-sm">{previewEvent.date}</p>
                           </div>
                        </div>
                        <div className="flex items-center gap-3">
                           <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                             <MapPin className="w-5 h-5" />
                           </div>
                           <div>
                             <p className="text-xs text-muted-foreground font-body">Lieu</p>
                             <p className="font-bold text-sm">{previewEvent.location}</p>
                           </div>
                        </div>
                        <div className="pt-4 border-t border-border mt-2">
                           <Button className="w-full shadow-md font-bold" disabled>
                             {(previewEvent.registrationType || "public") === "public" ? "Participer (Ouvert)" : "S'inscrire (Requis)"}
                           </Button>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-sm border-border/50">
                      <CardContent className="p-5 flex items-center gap-3">
                         <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center font-bold text-lg border border-border">
                            {previewEvent.partyAcronym.substring(0,3)}
                         </div>
                         <div>
                            <p className="text-xs text-muted-foreground font-body">Organisé par</p>
                            <p className="font-bold">{previewEvent.partyAcronym}</p>
                         </div>
                      </CardContent>
                    </Card>
                 </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal View Registrations */}
      <Dialog open={!!viewRegistrationsEventId} onOpenChange={(open) => !open && setViewRegistrationsEventId(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Liste des inscrits
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {registrations.length === 0 ? (
              <p className="text-center text-muted-foreground font-body py-8">Aucun inscrit pour le moment.</p>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg flex items-center justify-between mb-4">
                  <span className="font-body text-sm font-semibold text-foreground">Total: {registrations.length} inscrits</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="font-body text-xs border-green-200 text-green-700 hover:bg-green-50" onClick={() => {
                        const exportData = registrations.map(reg => ({
                          "Nom complet": reg.name,
                          "Email": reg.email,
                          "Téléphone": reg.phone || "-",
                          "Date d'inscription": new Date(reg.submittedAt).toLocaleDateString()
                        }));
                        generateExport(exportData, `inscrits_event_${viewRegistrationsEventId}`, 'Liste des inscrits à l\'événement', 'excel');
                    }}>Exporter (Excel)</Button>
                    <Button variant="outline" size="sm" className="font-body text-xs border-red-200 text-red-700 hover:bg-red-50" onClick={() => {
                        const exportData = registrations.map(reg => ({
                          "Nom complet": reg.name,
                          "Email": reg.email,
                          "Téléphone": reg.phone || "-",
                          "Date d'inscription": new Date(reg.submittedAt).toLocaleDateString()
                        }));
                        generateExport(exportData, `inscrits_event_${viewRegistrationsEventId}`, 'Liste des inscrits à l\'événement', 'pdf');
                    }}>Exporter (PDF)</Button>
                  </div>
                </div>
                {registrations.map(reg => (
                  <div key={reg.id} className="p-4 border border-border rounded-xl flex flex-col md:flex-row justify-between gap-4 bg-card">
                    <div>
                      <h4 className="font-bold text-sm font-display">{reg.name}</h4>
                      <p className="text-xs text-muted-foreground font-body">{reg.email}</p>
                      {reg.phone && <p className="text-xs text-muted-foreground font-body">{reg.phone}</p>}
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-muted-foreground">Inscrit le {new Date(reg.submittedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewRegistrationsEventId(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventsPage;
